import { useEffect, useState } from 'react';
import { Center } from '@chakra-ui/react';
import { Link } from '@chakra-ui/react';
import { Formik, Form, FormikConfig, FormikValues } from 'formik';
import { FormControl } from 'formik-chakra-ui';
import moment from 'moment';
import { CheckboxSingleControl, InputControl } from 'formik-chakra-ui';
import { signIn } from 'next-auth/client';
import { wait } from '@v1v2/util';

import {
  validateSchema,
  initFormikValuesFromSchema,
} from '@inspire/data/helpers/schemas';

import { EclaireurGeneralInfoSchema } from '@inspire/data/schemas/user/profile/eclaireur';
import { UserProfileSchema } from '@inspire/data/schemas/user/profile/index';
import { ExploreSchema } from '@inspire/data/schemas/user/profile/explore';
import Select from '@inspire/ui/chakra/forms/Select';
import DatePicker from '@inspire/ui/chakra/forms/DatePicker';
import { FormStack } from '@inspire/ui/chakra/forms/Form';
import { genderOptions } from '@inspire/ui/select-options';
import { ERROR_MESSAGE_EMAIL_EXISTS } from '@inspire/data/static/error-codes';
import StandaloneCheckbox from '@inspire/ui/chakra/forms/StandaloneCheckbox';

import { didntWorkToast, emailAdreadyUsedToast } from 'lib/toasts';
import { getAbsolutePath, QUESTIONNAIRE_PATH } from 'lib/paths';
import { handleErrors } from 'lib/errors';
import { useCreateLyceen, useMyProfile, useUser } from 'lib/hooks';
import { useMemoryStore } from 'lib/store';

import SubmitButton from 'components/forms/SubmitButton';
import {
  GTM_ACTION_INSCRIPTION_LYCEEN,
  GTM_ACTION_INSCRIPTION_REUSSIE,
  GTM_ACTION_SIGNUP_COMPLETED,
  GTM_CATEGORY_INSCRIPTION,
  GTM_CATEGORY_LYCEENS,
  pushEvent,
} from 'lib/gtm';
import TagManager from 'lib/packages/react-gtm-module/TagManager';
import { getCookie } from 'lib/cookies';

const SignupProfileSchema = UserProfileSchema.pick('codePostal')
  .extend(EclaireurGeneralInfoSchema.pick('birthDate'))
  .extend(ExploreSchema.pick('gender'))
  .requireFields();

let visitorId = '';

const SignupProfileForm = ({ onEmailAlreadyUsed }) => {
  const lyceenSignupClasse = useMemoryStore((s) => s.lyceenSignupClasse);
  const signupToken = lyceenSignupClasse?.signupToken;
  const { createLyceenAsync } = useCreateLyceen();
  const [etrangerCheckbox, setEtrangerCheckbox] = useState(false);
  const { myProfile, ssoId } = useMyProfile();
  const { user } = useUser();

  useEffect(() => {
    visitorId = getCookie('visitorId');
  });

  if (myProfile?.email !== undefined) {
    const formik: FormikConfig<FormikValues> = {
      initialValues: {
        ...initFormikValuesFromSchema(SignupProfileSchema),
        ...{
          birthDate: myProfile.birthDate
            ? moment(myProfile.birthDate).utcOffset(0, true).toDate()
            : undefined,
          codePostal: myProfile.codePostal,
          gender: myProfile.gender,
          newsletter: myProfile?.newsletter ?? false,
        },
      },
      validate: (values) => validateSchema(SignupProfileSchema, values),
      onSubmit: async (values) => {
        pushEvent(
          GTM_CATEGORY_INSCRIPTION,
          GTM_ACTION_INSCRIPTION_LYCEEN,
          'Clic valider page email'
        );
        // keep this event to maintain old GT stats
        pushEvent(GTM_CATEGORY_LYCEENS, GTM_ACTION_SIGNUP_COMPLETED);
        try {
          delete values.CGU;
          const res = await createLyceenAsync({
            profile: { ...values, newsletter: values.newsletter },
            signupToken,
          });
          // TODO: wrap createLyceen + login together
          if (res) {
            try {
              await signIn('credentials', {
                userId: res.createLyceen.id,
                ssoId: res.createLyceen.ssoId,
                callbackUrl: getAbsolutePath(QUESTIONNAIRE_PATH),
              });
            } catch (err) {
              console.error(err);
              didntWorkToast();
              throw err;
            }

            // Inscription réussie
            TagManager.dataLayer({
              dataLayer: {
                event: 'sso_inscription',
                eventCategory: GTM_CATEGORY_INSCRIPTION,
                eventAction: GTM_ACTION_INSCRIPTION_REUSSIE,
                eventLabel: undefined,
                eventValue: undefined,
                accountId: user?.id,
                ssoId: ssoId,
                visitorId: visitorId,
                profil: 'lyceen',
              },
            });

            // Keep the loading spinner during full page reload
            await wait(2000);
          }
        } catch (err) {
          console.error(err);
          handleErrors(err.errors, [
            ERROR_MESSAGE_EMAIL_EXISTS,
            () => emailAdreadyUsedToast(),
          ]);
          onEmailAlreadyUsed();
        }
        /*
      setLyceenSignupProfile(values);
      router.push({
        pathname: INSCRIPTION_LYCEEN_COMPTE_PATH,
        query: { ...router.query },
      });
      pushEvent(
        GTM_CATEGORY_INSCRIPTION,
        GTM_ACTION_INSCRIPTION_LYCEEN,
        'Clic valider page prénom'
      );*/
      },
    };

    return (
      <Formik {...formik}>
        {({ values, setFieldValue }) => (
          <Form>
            <FormStack>
              <FormControl
                name="birthDate"
                isRequired
                label="Date de naissance"
              >
                <DatePicker
                  id="birthDate"
                  placeholderText="jj/mm/aaaa"
                  dateFormat="dd/MM/yyyy"
                  selectedDate={values.birthDate}
                  openToDate={moment().subtract(17, 'years').toDate()}
                  onChange={(val) => {
                    if (!val) {
                      setFieldValue('birthDate', '');
                      return;
                    }
                    // transform the date to 0 utcOffset to avoid timezone issues, and remove
                    // any time from the date via startOf('day')
                    const simpleDate = moment(val)
                      .utcOffset(0, true)
                      .startOf('day')
                      .toDate();
                    setFieldValue('birthDate', simpleDate);
                  }}
                  utcOffset={moment().utcOffset()}
                  // @ts-ignore
                  autoComplete="off"
                />
              </FormControl>
              <FormControl
                name="gender"
                isRequired
                label="Tu es..."
                labelProps={{ htmlFor: 'react-select-gender-input' }}
              >
                <Select
                  instanceId="gender"
                  openMenuOnFocus
                  options={genderOptions}
                  onChange={(selectedOption) => {
                    setFieldValue('gender', selectedOption.value);
                  }}
                  value={genderOptions.find((o) => o.value === values.gender)}
                  isSearchable
                />
              </FormControl>
              <InputControl
                name="codePostal"
                isRequired
                label="Code postal"
                isDisabled={values.codePostal === '99999'}
                display={values.codePostal === '99999' ? 'none' : 'block'}
              />
              <StandaloneCheckbox
                label="J’habite à l’étranger"
                isChecked={etrangerCheckbox || values.codePostal === '99999'}
                onChange={async () => {
                  setFieldValue('codePostal', !etrangerCheckbox ? '99999' : '');
                  setEtrangerCheckbox(!etrangerCheckbox);
                }}
              />
              <CheckboxSingleControl
                name="newsletter"
                label="Je veux recevoir par email des conseils sur l'orientation post-bac, la vie étudiante et le monde du travail."
              />
              <CheckboxSingleControl isRequired name="CGU">
                <div>
                  <span> J'accepte les </span>
                  <Link
                    href="/infos-legales/conditions-generales"
                    target="_blank"
                  >
                    Conditions Générales d'Utilisation
                  </Link>
                </div>
              </CheckboxSingleControl>
              <Center>
                <SubmitButton size="lg">Valider</SubmitButton>
              </Center>
            </FormStack>
          </Form>
        )}
      </Formik>
    );
  }
  return null;
};

export default SignupProfileForm;
